/**
 * "Importar lista". build-prompt section 7.
 *
 * Never commits on upload. She sees a preview with a per-row status, fixes
 * what needs fixing, and only then commits. One bad row never fails the file.
 */

import { useEffect, useState } from 'react';
import { host, type ImportView } from '../api.js';
import { navigate } from '../router.js';
import { takeFile } from './pendingFile.js';
import { downloadTemplate } from './template.js';

export function Import({ eventId }: { eventId: string }) {
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [view, setView] = useState<ImportView | null>(null);
  const [merges, setMerges] = useState<number[][]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A list chosen on the create form, handed over once the party exists.
  useEffect(() => {
    const waiting = takeFile();
    if (waiting) void pick(waiting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function pick(file: File) {
    setFileName(file.name);
    setError(null);
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
      String.fromCharCode(...new Uint8Array(buffer)),
    );
    setFileBase64(base64);
    setMerges([]);

    setBusy(true);
    try {
      setView(await host.import(eventId, { file: base64 }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Re-read the same file with the families she joined, rather than editing
   * the preview in the browser. The server decides what a merge does, and the
   * screen only ever shows what an import would actually write.
   */
  async function applyMerges(next: number[][]) {
    if (!fileBase64) return;
    setBusy(true);
    try {
      setView(await host.import(eventId, { file: fileBase64, merges: next }));
      setMerges(next);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!fileBase64) return;
    setBusy(true);
    try {
      await host.import(eventId, { file: fileBase64, merges, commit: true });
      navigate(`/convites/${eventId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <a href={`/painel/${eventId}`}>← Painel</a>
      <h1>Importar lista</h1>
      <p className="muted">
        Aceita planilha do Excel, CSV ou TSV. Nada é salvo até você conferir.
      </p>
      <p className="muted">
        Pode mandar a lista que a escola enviou, do jeito que ela veio. A gente
        entende as colunas sozinho.
      </p>

      <button className="linkish" onClick={downloadTemplate}>
        Baixar modelo de planilha
      </button>

      {error && <div className="banner error">{error}</div>}

      <label htmlFor="file">Arquivo</label>
      <input
        id="file"
        type="file"
        accept=".xlsx,.csv,.tsv,text/csv,text/tab-separated-values"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void pick(file);
        }}
      />
      {fileName && <p className="muted">{fileName}</p>}

      {busy && <div className="spinner">Lendo a planilha…</div>}

      {view && !busy && (
        <Preview
          view={view}
          merges={merges}
          onMerge={(group) => void applyMerges([...merges, group])}
          onSplit={(group) =>
            void applyMerges(merges.filter((one) => !sameGroup(one, group)))
          }
          onCommit={commit}
        />
      )}
    </div>
  );
}

function sameGroup(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((row) => b.includes(row));
}

/**
 * Rows the importer thinks could be one family: same contact, written the same
 * way, a different child on each row. Twins in a class look exactly like this.
 * One group per set of rows, however many rows point at each other.
 */
function candidateGroups(view: ImportView): number[][] {
  const seen = new Set<string>();
  const groups: number[][] = [];

  for (const row of view.households) {
    if (row.mergeCandidates.length === 0) continue;
    const rows = [row.sourceRow, ...row.mergeCandidates].sort((a, b) => a - b);
    const key = rows.join('-');
    if (seen.has(key)) continue;
    seen.add(key);
    groups.push(rows);
  }

  return groups;
}

/** The contact the rows of a group have in common, for the muted line. */
function contactFor(view: ImportView, rows: readonly number[]): string {
  const row = view.households.find(
    (household) => household.sourceRow === rows[0],
  );
  if (!row?.contactName) return 'mesmo contato';
  return `${row.contactRole === 'father' ? 'pai' : 'mãe'}: ${row.contactName}`;
}

function labelsFor(view: ImportView, rows: readonly number[]): string {
  return rows
    .map(
      (row) =>
        view.households.find((household) => household.sourceRow === row)?.label ??
        `linha ${row}`,
    )
    .join(' e ');
}

function Preview({
  view,
  merges,
  onMerge,
  onSplit,
  onCommit,
}: {
  view: ImportView;
  merges: number[][];
  onMerge: (group: number[]) => void;
  onSplit: (group: number[]) => void;
  onCommit: () => void;
}) {
  const importable = view.households.filter(
    (row) => row.status === 'novo' || row.status === 'atualizado',
  );
  const problems = view.households.filter((row) => row.status === 'erro');
  const candidates = candidateGroups(view);

  return (
    <>
      <h2>O que encontramos</h2>
      <div className="counts">
        <div className="count">
          <div className="n">{view.counts.novo ?? 0}</div>
          <div className="label">novas</div>
        </div>
        <div className="count">
          <div className="n">{view.counts.atualizado ?? 0}</div>
          <div className="label">atualizadas</div>
        </div>
        <div className="count">
          <div className="n">{view.counts.duplicado ?? 0}</div>
          <div className="label">repetidas</div>
        </div>
        <div className="count">
          <div className="n">{view.counts.juntado ?? 0}</div>
          <div className="label">juntadas</div>
        </div>
        <div className="count">
          <div className="n">{view.counts.erro ?? 0}</div>
          <div className="label">precisam de você</div>
        </div>
      </div>

      {view.ignoredHeaders.length > 0 && (
        <div className="banner info" style={{ marginTop: '0.75rem' }}>
          Estas colunas não foram usadas: {view.ignoredHeaders.join(', ')}.
        </div>
      )}

      {view.unmappedHeaders.length > 0 && (
        <p className="muted">
          Colunas que não reconhecemos: {view.unmappedHeaders.join(', ')}.
        </p>
      )}

      {problems.length > 0 && (
        <>
          <h2>Precisam de você ({problems.length})</h2>
          <div className="card">
            {problems.map((row) => (
              <div className="row" key={row.sourceRow}>
                <div className="grow">
                  <div className="label-main">
                    linha {row.sourceRow}: {row.label || '(sem nome)'}
                  </div>
                  {row.issues.map((issue) => (
                    <div className="muted" key={issue.code}>
                      {issue.message}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {(candidates.length > 0 || merges.length > 0) && (
        <>
          <h2>Parecem irmãos</h2>
          <p className="muted">
            A mesma mãe ou o mesmo pai em mais de uma linha. Se for a mesma
            família, junte: um convite só, e os pais contam uma vez no buffet.
          </p>
          <div className="card">
            {candidates.map((group) => (
              <div className="row" key={`c${group.join('-')}`}>
                <div className="grow">
                  <div className="label-main">{labelsFor(view, group)}</div>
                  <div className="muted">{contactFor(view, group)}</div>
                </div>
                <button className="linkish" onClick={() => onMerge(group)}>
                  É uma família só
                </button>
              </div>
            ))}

            {merges.map((group) => (
              <div className="row" key={`m${group.join('-')}`}>
                <div className="grow">
                  <div className="label-main">
                    {view.households.find(
                      (row) => row.sourceRow === Math.min(...group),
                    )?.label ?? labelsFor(view, group)}
                  </div>
                  <div className="muted">
                    uma família só ·{' '}
                    {view.households.find(
                      (row) => row.sourceRow === Math.min(...group),
                    )?.people.length ?? 0}{' '}
                    pessoas
                  </div>
                </div>
                <button className="linkish" onClick={() => onSplit(group)}>
                  Separar
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <h2>Famílias ({importable.length})</h2>
      <div className="card">
        {importable.map((row) => (
          <div className="row" key={row.sourceRow}>
            <div className="grow">
              <div className="label-main">
                {row.label}
                {row.isHostFamily && ' · aniversariante'}
              </div>
              <div className="muted">
                {row.contactName ?? 'sem contato'} · {row.people.length} pessoas
                {row.issues.length > 0 && ` · ${row.issues[0]!.message}`}
              </div>
            </div>
            <span className={`tag ${row.status === 'novo' ? 'ok' : 'warn'}`}>
              {row.status}
            </span>
          </div>
        ))}
      </div>

      <div className="sticky-action">
        <button className="primary" onClick={onCommit} disabled={importable.length === 0}>
          Confirmar {importable.length} famílias
        </button>
      </div>
    </>
  );
}
